/**
 * Service for Profile Change Request API calls
 */

import { apiGet, apiPost, apiDelete } from '@/utils/api';

// Changeable fields enum (matching backend)
export enum ChangeableField {
  // User model fields (common)
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  PHONE = 'phone',

  // UserProfile model fields (common)
  FIRST_NAME_ARABIC = 'prenomArabe',
  LAST_NAME_ARABIC = 'nomArabe',
  ADDRESS = 'adresse',
  CITY = 'ville',
  POSTAL_CODE = 'codePostal',
  EMERGENCY_NAME = 'urgenceNom',
  EMERGENCY_PHONE = 'urgenceNumero',
  EMERGENCY_ADDRESS = 'urgenceAdresse',
  EMERGENCY_RELATION = 'urgenceRelation',

  // Student model fields (student-specific)
  STUDENT_CNIE = 'studentCnie',
  STUDENT_PASSPORT = 'studentPassport',
  STUDENT_DATE_OF_BIRTH = 'studentDateOfBirth',
  STUDENT_NATIONALITY = 'studentNationality',
  STUDENT_GENDER = 'studentGender',
  STUDENT_EMAIL = 'studentEmail',
  STUDENT_PHONE = 'studentPhone',
  STUDENT_ADDRESS = 'studentAddress',
  STUDENT_CITY = 'studentCity',
  STUDENT_POSTAL_CODE = 'studentPostalCode',
  STUDENT_MASSAR_CNE = 'studentMassarCNE',
  STUDENT_SERIE_BAC = 'studentSerieBac',
  STUDENT_ANNEE_BAC = 'studentAnneeBac',
  STUDENT_DIPLOME_ACCES = 'studentDiplomeAcces',
  STUDENT_SPECIALITE_DIPLOME = 'studentSpecialiteDiplome',
  STUDENT_MENTION_DIPLOME = 'studentMentionDiplome',
  STUDENT_LIEU_DIPLOME = 'studentLieuDiplome',
  STUDENT_RESIDENT = 'studentResident',
  STUDENT_FONCTIONNAIRE = 'studentFonctionnaire',
  STUDENT_BOURSE = 'studentBourse',
  STUDENT_MOBILITE = 'studentMobilite',

  // Staff-specific fields (UserProfile model)
  STAFF_CNIE = 'staffCnie',
  STAFF_PASSPORT = 'staffPassport',
  STAFF_DATE_OF_BIRTH = 'staffDateOfBirth',
  STAFF_NATIONALITY = 'staffNationality',
  STAFF_GENDER = 'staffGender',
  STAFF_HANDICAP = 'staffHandicap',
  STAFF_RIB = 'staffRib',
  STAFF_SALARY = 'staffSalary',
  STAFF_FONCTION = 'staffFonction',
  STAFF_DIPLOME = 'staffDiplome',
  STAFF_DATE_RECRUTEMENT = 'staffDateRecrutement',
  // Professor-specific fields
  STAFF_STATUT_PROFESSEUR = 'staffStatutProfesseur',
  STAFF_DEPARTEMENT = 'staffDepartement',
  STAFF_EST_PUBLIC = 'staffEstPublic',
  STAFF_ETABLISSEMENT_ORIGINE = 'staffEtablissementOrigine',
  STAFF_GRADE = 'staffGrade',
  STAFF_SPECIALITE = 'staffSpecialite',
  STAFF_MATIERES = 'staffMatieres',
  STAFF_TAUX_HORAIRE = 'staffTauxHoraire',

  // Document fields (for requesting document modifications)
  DOCUMENT_CIN_FRONT = 'documentCinFront',
  DOCUMENT_CIN_BACK = 'documentCinBack',
  DOCUMENT_PASSPORT = 'documentPassport',
  DOCUMENT_CV = 'documentCv',
  DOCUMENT_DIPLOME = 'documentDiplome',
  DOCUMENT_PHOTO = 'documentPhoto',
  DOCUMENT_RIB = 'documentRib',
}

// Human-readable labels for fields (French)
export const FIELD_LABELS: Record<ChangeableField, string> = {
  // Common fields
  [ChangeableField.FIRST_NAME]: 'Prénom',
  [ChangeableField.LAST_NAME]: 'Nom',
  [ChangeableField.FIRST_NAME_ARABIC]: 'Prénom (Arabe)',
  [ChangeableField.LAST_NAME_ARABIC]: 'Nom (Arabe)',
  [ChangeableField.PHONE]: 'Téléphone',
  [ChangeableField.ADDRESS]: 'Adresse',
  [ChangeableField.CITY]: 'Ville',
  [ChangeableField.POSTAL_CODE]: 'Code postal',
  [ChangeableField.EMERGENCY_NAME]: 'Contact d\'urgence - Nom',
  [ChangeableField.EMERGENCY_PHONE]: 'Contact d\'urgence - Téléphone',
  [ChangeableField.EMERGENCY_ADDRESS]: 'Contact d\'urgence - Adresse',
  [ChangeableField.EMERGENCY_RELATION]: 'Contact d\'urgence - Relation',

  // Student-specific fields
  [ChangeableField.STUDENT_CNIE]: 'CNIE',
  [ChangeableField.STUDENT_PASSPORT]: 'Passeport',
  [ChangeableField.STUDENT_DATE_OF_BIRTH]: 'Date de naissance',
  [ChangeableField.STUDENT_NATIONALITY]: 'Nationalité',
  [ChangeableField.STUDENT_GENDER]: 'Sexe',
  [ChangeableField.STUDENT_EMAIL]: 'Email',
  [ChangeableField.STUDENT_PHONE]: 'Téléphone',
  [ChangeableField.STUDENT_ADDRESS]: 'Adresse',
  [ChangeableField.STUDENT_CITY]: 'Ville',
  [ChangeableField.STUDENT_POSTAL_CODE]: 'Code postal',
  [ChangeableField.STUDENT_MASSAR_CNE]: 'N° Massar/CNE',
  [ChangeableField.STUDENT_SERIE_BAC]: 'Série du Baccalauréat',
  [ChangeableField.STUDENT_ANNEE_BAC]: 'Année d\'obtention du Bac',
  [ChangeableField.STUDENT_DIPLOME_ACCES]: 'Diplôme d\'accès',
  [ChangeableField.STUDENT_SPECIALITE_DIPLOME]: 'Spécialité du diplôme',
  [ChangeableField.STUDENT_MENTION_DIPLOME]: 'Mention du diplôme',
  [ChangeableField.STUDENT_LIEU_DIPLOME]: 'Lieu d\'obtention du diplôme',
  [ChangeableField.STUDENT_RESIDENT]: 'Résident',
  [ChangeableField.STUDENT_FONCTIONNAIRE]: 'Fonctionnaire',
  [ChangeableField.STUDENT_BOURSE]: 'Pourcentage de bourse',
  [ChangeableField.STUDENT_MOBILITE]: 'Mobilité',

  // Staff-specific fields
  [ChangeableField.STAFF_CNIE]: 'CNIE',
  [ChangeableField.STAFF_PASSPORT]: 'Passeport',
  [ChangeableField.STAFF_DATE_OF_BIRTH]: 'Date de naissance',
  [ChangeableField.STAFF_NATIONALITY]: 'Nationalité',
  [ChangeableField.STAFF_GENDER]: 'Sexe',
  [ChangeableField.STAFF_HANDICAP]: 'Situation de handicap',
  [ChangeableField.STAFF_RIB]: 'RIB',
  [ChangeableField.STAFF_SALARY]: 'Salaire mensuel net',
  [ChangeableField.STAFF_FONCTION]: 'Fonction',
  [ChangeableField.STAFF_DIPLOME]: 'Diplôme',
  [ChangeableField.STAFF_DATE_RECRUTEMENT]: 'Date de recrutement',
  // Professor-specific fields
  [ChangeableField.STAFF_STATUT_PROFESSEUR]: 'Statut professeur',
  [ChangeableField.STAFF_DEPARTEMENT]: 'Département d\'attache',
  [ChangeableField.STAFF_EST_PUBLIC]: 'Originaire établissement public',
  [ChangeableField.STAFF_ETABLISSEMENT_ORIGINE]: 'Établissement d\'origine',
  [ChangeableField.STAFF_GRADE]: 'Grade',
  [ChangeableField.STAFF_SPECIALITE]: 'Spécialité',
  [ChangeableField.STAFF_MATIERES]: 'Matières enseignées',
  [ChangeableField.STAFF_TAUX_HORAIRE]: 'Taux horaire',

  // Document fields
  [ChangeableField.DOCUMENT_CIN_FRONT]: 'Carte d\'identité (Recto)',
  [ChangeableField.DOCUMENT_CIN_BACK]: 'Carte d\'identité (Verso)',
  [ChangeableField.DOCUMENT_PASSPORT]: 'Passeport',
  [ChangeableField.DOCUMENT_CV]: 'CV',
  [ChangeableField.DOCUMENT_DIPLOME]: 'Diplôme',
  [ChangeableField.DOCUMENT_PHOTO]: 'Photo d\'identité',
  [ChangeableField.DOCUMENT_RIB]: 'RIB',
};

// Student changeable fields list (for UI)
export const STUDENT_CHANGEABLE_FIELDS: ChangeableField[] = [
  // Identity
  ChangeableField.FIRST_NAME,
  ChangeableField.LAST_NAME,
  ChangeableField.FIRST_NAME_ARABIC,
  ChangeableField.LAST_NAME_ARABIC,
  ChangeableField.STUDENT_CNIE,
  ChangeableField.STUDENT_PASSPORT,
  ChangeableField.STUDENT_DATE_OF_BIRTH,
  ChangeableField.STUDENT_NATIONALITY,
  ChangeableField.STUDENT_GENDER,
  // Contact
  ChangeableField.STUDENT_EMAIL,
  ChangeableField.STUDENT_PHONE,
  ChangeableField.STUDENT_ADDRESS,
  ChangeableField.STUDENT_CITY,
  ChangeableField.STUDENT_POSTAL_CODE,
  // Emergency contact
  ChangeableField.EMERGENCY_NAME,
  ChangeableField.EMERGENCY_PHONE,
  ChangeableField.EMERGENCY_ADDRESS,
  ChangeableField.EMERGENCY_RELATION,
  // Academic
  ChangeableField.STUDENT_MASSAR_CNE,
  // Diploma
  ChangeableField.STUDENT_SERIE_BAC,
  ChangeableField.STUDENT_ANNEE_BAC,
  ChangeableField.STUDENT_DIPLOME_ACCES,
  ChangeableField.STUDENT_SPECIALITE_DIPLOME,
  ChangeableField.STUDENT_MENTION_DIPLOME,
  ChangeableField.STUDENT_LIEU_DIPLOME,
  // Status
  ChangeableField.STUDENT_RESIDENT,
  ChangeableField.STUDENT_FONCTIONNAIRE,
  ChangeableField.STUDENT_BOURSE,
  ChangeableField.STUDENT_MOBILITE,
];

// Staff changeable fields list (for UI) - for all staff roles
export const STAFF_CHANGEABLE_FIELDS: ChangeableField[] = [
  // Identity
  ChangeableField.FIRST_NAME,
  ChangeableField.LAST_NAME,
  ChangeableField.FIRST_NAME_ARABIC,
  ChangeableField.LAST_NAME_ARABIC,
  ChangeableField.STAFF_CNIE,
  ChangeableField.STAFF_PASSPORT,
  ChangeableField.STAFF_DATE_OF_BIRTH,
  ChangeableField.STAFF_NATIONALITY,
  ChangeableField.STAFF_GENDER,
  ChangeableField.STAFF_HANDICAP,
  // Contact
  ChangeableField.PHONE,
  ChangeableField.ADDRESS,
  ChangeableField.CITY,
  ChangeableField.POSTAL_CODE,
  // Emergency contact
  ChangeableField.EMERGENCY_NAME,
  ChangeableField.EMERGENCY_PHONE,
  ChangeableField.EMERGENCY_ADDRESS,
  ChangeableField.EMERGENCY_RELATION,
  // Professional
  ChangeableField.STAFF_FONCTION,
  ChangeableField.STAFF_DIPLOME,
  ChangeableField.STAFF_DATE_RECRUTEMENT,
  ChangeableField.STAFF_RIB,
  ChangeableField.STAFF_SALARY,
  // Documents
  ChangeableField.DOCUMENT_CIN_FRONT,
  ChangeableField.DOCUMENT_CIN_BACK,
  ChangeableField.DOCUMENT_PASSPORT,
  ChangeableField.DOCUMENT_CV,
  ChangeableField.DOCUMENT_DIPLOME,
  ChangeableField.DOCUMENT_PHOTO,
  ChangeableField.DOCUMENT_RIB,
];

// Professor-specific changeable fields (additional to staff fields)
export const PROFESSOR_CHANGEABLE_FIELDS: ChangeableField[] = [
  ...STAFF_CHANGEABLE_FIELDS,
  // Professor-specific
  ChangeableField.STAFF_STATUT_PROFESSEUR,
  ChangeableField.STAFF_DEPARTEMENT,
  ChangeableField.STAFF_EST_PUBLIC,
  ChangeableField.STAFF_ETABLISSEMENT_ORIGINE,
  ChangeableField.STAFF_GRADE,
  ChangeableField.STAFF_SPECIALITE,
  // STAFF_MATIERES excluded: system-generated from course assignments
  // STAFF_TAUX_HORAIRE excluded: set by the administration (DG, DP, RF, BM)
];

export type ChangeRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ChangeRequest {
  id: string;
  userId: string;
  fieldName: string;
  fieldLabel: string;
  currentValue: string | null;
  requestedValue: string;
  reason?: string | null;
  status: ChangeRequestStatus;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewComment?: string | null;
  createdAt: string;
  updatedAt: string;
  // Admin view includes user info
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  reviewer?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface CreateChangeRequestDto {
  fieldName: ChangeableField;
  requestedValue: string;
  reason?: string;
}

export interface ReviewChangeRequestDto {
  decision: 'APPROVED' | 'REJECTED';
  comment?: string;
}

/**
 * Create a profile change request
 */
export async function createChangeRequest(dto: CreateChangeRequestDto): Promise<ChangeRequest> {
  return apiPost<ChangeRequest>('/profile/change-requests', dto);
}

/**
 * Get all change requests for the current user
 */
export async function getMyChangeRequests(): Promise<ChangeRequest[]> {
  return apiGet<ChangeRequest[]>('/profile/change-requests');
}

/**
 * Cancel a pending change request
 */
export async function cancelChangeRequest(requestId: string): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(`/profile/change-requests/${requestId}`);
}

// ==================== ADMIN ENDPOINTS ====================

export type UserTypeFilter = 'staff' | 'student' | 'all';

/**
 * Get pending change requests (admin)
 * @param userType - Filter by user type: 'staff', 'student', or 'all'
 */
export async function getPendingChangeRequests(userType?: UserTypeFilter): Promise<ChangeRequest[]> {
  const params = new URLSearchParams();
  if (userType) params.append('userType', userType);

  const queryString = params.toString();
  const endpoint = queryString
    ? `/profile/admin/change-requests/pending?${queryString}`
    : '/profile/admin/change-requests/pending';

  return apiGet<ChangeRequest[]>(endpoint);
}

/**
 * Get all change requests with optional status and userType filter (admin)
 * @param status - Filter by status
 * @param userType - Filter by user type: 'staff', 'student', or 'all'
 */
export async function getAllChangeRequests(status?: ChangeRequestStatus, userType?: UserTypeFilter): Promise<ChangeRequest[]> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (userType) params.append('userType', userType);

  const queryString = params.toString();
  const endpoint = queryString
    ? `/profile/admin/change-requests?${queryString}`
    : '/profile/admin/change-requests';

  return apiGet<ChangeRequest[]>(endpoint);
}

/**
 * Review (approve/reject) a change request (admin)
 */
export async function reviewChangeRequest(
  requestId: string,
  dto: ReviewChangeRequestDto,
): Promise<ChangeRequest> {
  return apiPost<ChangeRequest>(`/profile/admin/change-requests/${requestId}/review`, dto);
}

// ==================== USER DOCUMENTS (ADMIN) ====================

export interface UserDocument {
  id: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  isVerified: boolean;
  verifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get user documents for admin review (admin)
 */
export async function getAdminUserDocuments(userId: string): Promise<UserDocument[]> {
  return apiGet<UserDocument[]>(`/profile/admin/users/${userId}/documents`);
}

/**
 * Get pending change requests count (admin)
 */
export async function getPendingChangeRequestsCount(): Promise<number> {
  const requests = await getPendingChangeRequests();
  return requests.length;
}
